import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';

type SizeNameOverrides = {
  [sizeKey: string]: boolean;
};

type SizeNameOverridesContextValue = {
  overriddenSizeNames: SizeNameOverrides;
  setSizeNameOverridden: (sizeKey: string, isOverridden: boolean) => void;
};

export const SizeNameOverridesContext =
  createContext<SizeNameOverridesContextValue | null>(null);

export const useSizeNameOverride = (sizeKey: string) => {
  const context = useContext(SizeNameOverridesContext);

  return [
    Boolean(context?.overriddenSizeNames[sizeKey]),
    (isOverridden: boolean) =>
      context?.setSizeNameOverridden(sizeKey, isOverridden),
  ] as const;
};

const SizeNameOverridesContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [overriddenSizeNames, setOverriddenSizeNames] =
    useState<SizeNameOverrides>({});

  const setSizeNameOverridden = useCallback(
    (sizeKey: string, isOverridden: boolean) => {
      setOverriddenSizeNames(prevOverriddenSizeNames => ({
        ...prevOverriddenSizeNames,
        [sizeKey]: isOverridden,
      }));
    },
    []
  );

  return (
    <SizeNameOverridesContext.Provider
      value={{ overriddenSizeNames, setSizeNameOverridden }}
    >
      {children}
    </SizeNameOverridesContext.Provider>
  );
};

export default SizeNameOverridesContextProvider;
